// Purchasing for the Admin (M6, WP6.2): suppliers and their items, price lists, purchase
// requests, purchase orders, goods receipts, restock levels and the restock suggestion. URL
// search params, API calls and TanStack Query options; every response is parsed with the
// shared @enp/domain/api schema (lib/api.ts).

import {
  type CatalogueReviewItem,
  type PoUpdateRequest,
  type PriceListCreateRequest,
  type PurchaseRequestCreateRequest,
  type RestockLevelUpdateRequest,
  type SupplierCreateRequest,
  type SupplierItemCreateRequest,
  type SupplierItemReplaceRequest,
  type SupplierItemUpdateRequest,
  type SupplierUpdateRequest,
  catalogueProductListResponseSchema,
  catalogueReviewItemListResponseSchema,
  catalogueReviewItemSchema,
  heldDeliveryListResponseSchema,
  poDetailSchema,
  poListResponseSchema,
  priceListDetailSchema,
  priceListListResponseSchema,
  purchaseRequestListResponseSchema,
  purchaseRequestSchema,
  receiptDetailSchema,
  receiptListResponseSchema,
  restockLevelListResponseSchema,
  restockResponseSchema,
  supplierDetailSchema,
  supplierItemSchema,
  supplierListResponseSchema,
} from '@enp/domain/api';
import { PO_STATUSES, PURCHASE_REQUEST_STATUSES } from '@enp/domain/purchasing';
import { isPlainDate } from '@enp/domain/time';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  type QueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../lib/api.js';
import { clean, orderedRange, PAGE_SIZE } from '../stock/queries.js';

const plainDate = z.string().refine(isPlainDate);
const optionalDay = plainDate.optional().catch(undefined);
const optionalId = z.uuid().optional().catch(undefined);
/** A text search in the URL (a number typed is still text). */
const text = z
  .preprocess((v) => (typeof v === 'number' ? String(v) : v), z.string().trim().min(1).max(100))
  .optional()
  .catch(undefined);

const enc = encodeURIComponent;

export const purchasingKeys = {
  all: ['purchasing'] as const,
  suppliers: (search: SuppliersSearch) => ['purchasing', 'suppliers', clean(search)] as const,
  supplier: (id: string) => ['purchasing', 'supplier', id] as const,
  priceLists: (supplierId: string) => ['purchasing', 'price-lists', supplierId] as const,
  priceList: (id: string) => ['purchasing', 'price-list', id] as const,
  requests: (search: RequestsSearch) => ['purchasing', 'requests', clean(search)] as const,
  request: (id: string) => ['purchasing', 'request', id] as const,
  orders: (search: OrdersSearch) => ['purchasing', 'orders', clean(search)] as const,
  order: (id: string) => ['purchasing', 'order', id] as const,
  receipts: (search: ReceiptsSearch) => ['purchasing', 'receipts', clean(search)] as const,
  receipt: (id: string) => ['purchasing', 'receipt', id] as const,
  held: (po?: string) => ['purchasing', 'held-deliveries', po ?? null] as const,
  restock: (search: RestockSearch) =>
    ['purchasing', 'restock', clean({ ...search, all: undefined })] as const,
  levels: () => ['purchasing', 'restock-levels'] as const,
  reviews: () => ['purchasing', 'review-items'] as const,
};

/**
 * After any purchasing change: everything purchasing shows is read again, and the approvals
 * inbox (a request waits there) and stock (a receipt moves it) too.
 */
export const invalidatePurchasing = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: purchasingKeys.all }),
    queryClient.invalidateQueries({ queryKey: ['stock-control'] }),
  ]);

// ---------------------------------------------------------------- suppliers

export const suppliersSearchSchema = z.object({
  q: text,
  /** Show archived suppliers instead of live ones. */
  archived: z.literal(true).optional().catch(undefined),
});
export type SuppliersSearch = z.infer<typeof suppliersSearchSchema>;

export const suppliersQuery = (search: SuppliersSearch) =>
  infiniteQueryOptions({
    queryKey: purchasingKeys.suppliers(search),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (search.q) params.set('q', search.q);
      if (search.archived) params.set('archived', 'true');
      if (pageParam) params.set('cursor', pageParam);
      return api(`/purchasing/suppliers?${params.toString()}`, {
        signal,
        schema: supplierListResponseSchema,
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
    retry: false,
  });

/** Every live supplier (for pickers), up to the largest page. */
export const allSuppliersQuery = () =>
  queryOptions({
    queryKey: [...purchasingKeys.all, 'supplier-options'] as const,
    queryFn: ({ signal }) =>
      api('/purchasing/suppliers?limit=200', { signal, schema: supplierListResponseSchema }),
    staleTime: 60_000,
    retry: false,
  });

export const supplierQuery = (id: string) =>
  queryOptions({
    queryKey: purchasingKeys.supplier(id),
    queryFn: ({ signal }) =>
      api(`/purchasing/suppliers/${enc(id)}`, { signal, schema: supplierDetailSchema }),
    retry: false,
  });

// Creates are retry-safe (D-063): each form makes one key when it opens and sends it with
// every attempt, so a repeat answers the first result (server/src/http/idempotency.ts).
const once = (idempotencyKey: string | undefined) =>
  idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {};

export const createSupplier = (body: SupplierCreateRequest, idempotencyKey?: string) =>
  api('/purchasing/suppliers', {
    method: 'POST',
    body,
    schema: supplierDetailSchema,
    ...once(idempotencyKey),
  });

export const updateSupplier = (id: string, body: SupplierUpdateRequest) =>
  api(`/purchasing/suppliers/${enc(id)}`, { method: 'PUT', body, schema: supplierDetailSchema });

export const createSupplierItem = (
  supplierId: string,
  body: SupplierItemCreateRequest,
  idempotencyKey?: string,
) =>
  api(`/purchasing/suppliers/${enc(supplierId)}/items`, {
    method: 'POST',
    body,
    schema: supplierItemSchema,
    ...once(idempotencyKey),
  });

/** "Replace with a new pack": the pack of an item in use never changes (D-063). */
export const replaceSupplierItem = (
  id: string,
  body: SupplierItemReplaceRequest,
  idempotencyKey?: string,
) =>
  api(`/purchasing/supplier-items/${enc(id)}/replace`, {
    method: 'POST',
    body,
    schema: supplierItemSchema,
    ...once(idempotencyKey),
  });

export const updateSupplierItem = (id: string, body: SupplierItemUpdateRequest) =>
  api(`/purchasing/supplier-items/${enc(id)}`, {
    method: 'PUT',
    body,
    schema: supplierItemSchema,
  });

/** Products to buy, by name (the catalogue's search). */
export const productSearchQuery = (q: string) =>
  queryOptions({
    queryKey: [...purchasingKeys.all, 'product-search', q] as const,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: '20', sort: 'name' });
      if (q) params.set('q', q);
      return api(`/catalogue/products?${params.toString()}`, {
        signal,
        schema: catalogueProductListResponseSchema,
      });
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  });

/**
 * Open import changes to review (D-058/D-060): an import wanted to change a supplier field a
 * person had edited. Read from the catalogue's review queue (first page of the largest size).
 */
export const openReviewItemsQuery = () =>
  queryOptions({
    queryKey: purchasingKeys.reviews(),
    queryFn: ({ signal }) =>
      api('/catalogue/review-items?status=open&limit=200', {
        signal,
        schema: catalogueReviewItemListResponseSchema,
      }),
    retry: false,
  });

/** The open review items of one supplier. */
export const supplierReviewItems = (
  items: readonly CatalogueReviewItem[] | undefined,
  supplierId: string,
) => (items ?? []).filter((i) => i.entity.table === 'supplier' && i.entity.id === supplierId);

export const resolveReviewItem = (id: string, resolution: 'keep' | 'take') =>
  api(`/catalogue/review-items/${enc(id)}/resolve`, {
    method: 'POST',
    body: { resolution },
    schema: catalogueReviewItemSchema,
  });

// ---------------------------------------------------------------- price lists

export const priceListsQuery = (supplierId: string) =>
  queryOptions({
    queryKey: purchasingKeys.priceLists(supplierId),
    queryFn: ({ signal }) =>
      api(`/purchasing/suppliers/${enc(supplierId)}/price-lists`, {
        signal,
        schema: priceListListResponseSchema,
      }),
    retry: false,
  });

export const priceListQuery = (id: string) =>
  queryOptions({
    queryKey: purchasingKeys.priceList(id),
    queryFn: ({ signal }) =>
      api(`/purchasing/price-lists/${enc(id)}`, { signal, schema: priceListDetailSchema }),
    retry: false,
  });

export const createPriceList = (
  supplierId: string,
  body: PriceListCreateRequest,
  idempotencyKey?: string,
) =>
  api(`/purchasing/suppliers/${enc(supplierId)}/price-lists`, {
    method: 'POST',
    body,
    schema: priceListDetailSchema,
    ...once(idempotencyKey),
  });

export const archivePriceList = (id: string) =>
  api(`/purchasing/price-lists/${enc(id)}/archive`, {
    method: 'POST',
    schema: priceListDetailSchema,
  });

// ---------------------------------------------------------------- purchase requests

export const requestsSearchSchema = z.object({
  status: z.enum(PURCHASE_REQUEST_STATUSES).optional().catch(undefined),
  supplier: optionalId,
});
export type RequestsSearch = z.infer<typeof requestsSearchSchema>;

export const requestsQuery = (search: RequestsSearch) =>
  infiniteQueryOptions({
    queryKey: purchasingKeys.requests(search),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (search.status) params.set('status', search.status);
      if (search.supplier) params.set('supplier', search.supplier);
      if (pageParam) params.set('cursor', pageParam);
      return api(`/purchasing/requests?${params.toString()}`, {
        signal,
        schema: purchaseRequestListResponseSchema,
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
    retry: false,
  });

export const requestQuery = (id: string) =>
  queryOptions({
    queryKey: purchasingKeys.request(id),
    queryFn: ({ signal }) =>
      api(`/purchasing/requests/${enc(id)}`, { signal, schema: purchaseRequestSchema }),
    retry: false,
  });

export const createRequest = (body: PurchaseRequestCreateRequest, idempotencyKey?: string) =>
  api('/purchasing/requests', {
    method: 'POST',
    body,
    schema: purchaseRequestSchema,
    ...once(idempotencyKey),
  });

/** The new-request page's state in the URL: which supplier is picked. */
export const newRequestSearchSchema = z.object({ supplier: optionalId });

// ---------------------------------------------------------------- purchase orders

/** The orders list's views: still expecting deliveries (default), one status, or all. */
export const ORDER_VIEWS = ['expected', ...PO_STATUSES, 'every'] as const;
export type OrderView = (typeof ORDER_VIEWS)[number];

export const ordersSearchSchema = z.object({
  view: z.enum(ORDER_VIEWS).optional().catch(undefined),
  supplier: optionalId,
  from: optionalDay,
  to: optionalDay,
});
export type OrdersSearch = z.infer<typeof ordersSearchSchema>;

export function ordersQueryString(search: OrdersSearch, cursor: string | null): string {
  const s = orderedRange(search);
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
  const view = s.view ?? 'expected';
  if (view === 'expected') params.set('open', 'true');
  else if (view !== 'every') params.set('status', view);
  if (s.supplier) params.set('supplier', s.supplier);
  if (s.from) params.set('expected_from', s.from);
  if (s.to) params.set('expected_to', s.to);
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

export const ordersQuery = (search: OrdersSearch) =>
  infiniteQueryOptions({
    queryKey: purchasingKeys.orders(search),
    queryFn: ({ pageParam, signal }) =>
      api(`/purchasing/orders?${ordersQueryString(search, pageParam)}`, {
        signal,
        schema: poListResponseSchema,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
    retry: false,
  });

export const orderQuery = (id: string) =>
  queryOptions({
    queryKey: purchasingKeys.order(id),
    queryFn: ({ signal }) =>
      api(`/purchasing/orders/${enc(id)}`, { signal, schema: poDetailSchema }),
    retry: false,
  });

export const updateOrder = (id: string, body: PoUpdateRequest) =>
  api(`/purchasing/orders/${enc(id)}`, { method: 'PUT', body, schema: poDetailSchema });

export const markOrderSent = (id: string, note: string | undefined) =>
  api(`/purchasing/orders/${enc(id)}/send`, {
    method: 'POST',
    body: note ? { note } : {},
    schema: poDetailSchema,
  });

export const endOrder = (id: string, how: 'cancel' | 'close', reason: string) =>
  api(`/purchasing/orders/${enc(id)}/${how}`, {
    method: 'POST',
    body: { reason },
    schema: poDetailSchema,
  });

// ---------------------------------------------------------------- receipts

export const receiptsSearchSchema = z.object({
  from: optionalDay,
  to: optionalDay,
  supplier: optionalId,
  po: optionalId,
  /** Only receipts with something flagged. */
  flagged: z.literal(true).optional().catch(undefined),
});
export type ReceiptsSearch = z.infer<typeof receiptsSearchSchema>;

export function receiptsQueryString(search: ReceiptsSearch, cursor: string | null): string {
  const s = orderedRange(search);
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (s.from) params.set('from', s.from);
  if (s.to) params.set('to', s.to);
  if (s.supplier) params.set('supplier', s.supplier);
  if (s.po) params.set('po', s.po);
  if (s.flagged) params.set('flagged', 'true');
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

export const receiptsQuery = (search: ReceiptsSearch) =>
  infiniteQueryOptions({
    queryKey: purchasingKeys.receipts(search),
    queryFn: ({ pageParam, signal }) =>
      api(`/purchasing/receipts?${receiptsQueryString(search, pageParam)}`, {
        signal,
        schema: receiptListResponseSchema,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
    retry: false,
  });

export const receiptQuery = (id: string) =>
  queryOptions({
    queryKey: purchasingKeys.receipt(id),
    queryFn: ({ signal }) =>
      api(`/purchasing/receipts/${enc(id)}`, { signal, schema: receiptDetailSchema }),
    retry: false,
  });

/**
 * Deliveries held for a manager (D-066, UI6A-102): recorded at a station, not yet booked. With
 * `po`, those received against that order. The goods are there, but not in stock yet.
 */
export const heldDeliveriesQuery = (po?: string) =>
  queryOptions({
    queryKey: purchasingKeys.held(po),
    queryFn: ({ signal }) =>
      api(`/purchasing/receipts/held${po ? `?po=${enc(po)}` : ''}`, {
        signal,
        schema: heldDeliveryListResponseSchema,
      }),
    retry: false,
  });

// ---------------------------------------------------------------- restock

/**
 * The restock suggestion's inputs in the URL: whose history (a replay scenario, else a real
 * branch), the last day and length of the window, the order cycle and safety days, a
 * supplier, and whether covered products are listed too.
 */
export const restockSearchSchema = z.object({
  scenario: optionalId,
  branch: optionalId,
  as_of: optionalDay,
  days: z.coerce.number().int().min(7).max(365).optional().catch(undefined),
  review: z.coerce.number().int().min(0).max(90).optional().catch(undefined),
  safety: z.coerce.number().int().min(0).max(90).optional().catch(undefined),
  supplier: optionalId,
  all: z.literal(true).optional().catch(undefined),
});
export type RestockSearch = z.infer<typeof restockSearchSchema>;

export function restockQueryString(search: RestockSearch): string {
  const params = new URLSearchParams();
  if (search.scenario) params.set('replay_scenario_id', search.scenario);
  else if (search.branch) params.set('branch_id', search.branch);
  if (search.as_of) params.set('as_of', search.as_of);
  if (search.days !== undefined) params.set('days', String(search.days));
  if (search.review !== undefined) params.set('review_days', String(search.review));
  if (search.safety !== undefined) params.set('safety_days', String(search.safety));
  if (search.supplier) params.set('supplier_id', search.supplier);
  // Always every product: a short one or one without sales data must never hide behind
  // "nothing to order" (UI6A-3). `all` only decides whether covered ones are listed.
  params.set('include_zero', 'true');
  return params.toString();
}

export const restockQuery = (search: RestockSearch) =>
  queryOptions({
    queryKey: purchasingKeys.restock(search),
    queryFn: ({ signal }) => {
      const qs = restockQueryString(search);
      return api(`/purchasing/restock${qs ? `?${qs}` : ''}`, {
        signal,
        schema: restockResponseSchema,
      });
    },
    placeholderData: keepPreviousData,
    retry: false,
  });

/** Min / par levels for every branch (the all-branch rows and each branch's own). */
export const restockLevelsQuery = () =>
  queryOptions({
    queryKey: purchasingKeys.levels(),
    queryFn: ({ signal }) =>
      api('/purchasing/restock-levels', { signal, schema: restockLevelListResponseSchema }),
    retry: false,
  });

export const setRestockLevel = (body: RestockLevelUpdateRequest) =>
  api('/purchasing/restock-levels', {
    method: 'PUT',
    body,
    schema: restockLevelListResponseSchema,
  });
